import { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Tabs,
  Tab,
  TextField,
  Button,
} from "@mui/material";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import DeleteIcon from "@mui/icons-material/Delete";

const LANGS = [
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "it", label: "Italiano" },
  { code: "en", label: "English" },
];

function BlacklistAdmin() {
  const [blacklist, setBlacklist] = useState({
    fr: [],
    de: [],
    it: [],
    en: [],
  });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("fr");
  const [newPhrase, setNewPhrase] = useState("");

  const fetchBlacklist = async () => {
    setLoading(true);
    try {
      const response = await fetchWithAuth("/blacklist/", { method: "GET" });
      const data = await response.json();
      setBlacklist({
        fr: data?.fr || [],
        de: data?.de || [],
        it: data?.it || [],
        en: data?.en || [],
      });
    } catch (error) {
      setBlacklist({ fr: [], de: [], it: [], en: [] });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchBlacklist();
  }, []);

  const handleTabChange = (event, newValue) => setTab(newValue);

  const handleAddPhrase = async () => {
    if (!newPhrase.trim()) return;
    setLoading(true);
    try {
      const response = await fetchWithAuth("/blacklist/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lang: tab, phrase: newPhrase }),
      });
      if (response.ok) {
        setNewPhrase("");
        fetchBlacklist();
      }
    } catch {}
    setLoading(false);
  };

  const handleDeletePhrase = async (phrase) => {
    setLoading(true);
    try {
      const response = await fetchWithAuth("/blacklist/", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lang: tab, phrase }),
      });
      if (response.ok) fetchBlacklist();
    } catch {}
    setLoading(false);
  };

  return (
    <>
      <Tabs
        value={tab}
        onChange={handleTabChange}
        centered
        sx={{
          mb: 2,
          ".MuiTabs-flexContainer": { flexWrap: "wrap" },
        }}
      >
        {LANGS.map((l) => (
          <Tab
            key={l.code}
            value={l.code}
            label={l.label}
            sx={{ fontSize: { xs: "1rem", md: "1.1rem" } }}
          />
        ))}
      </Tabs>
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        gap={2}
        justifyContent="center"
        alignItems="center"
        mb={3}
        width="100%"
      >
        <TextField
          label={`Nouvelle phrase (${LANGS.find((l) => l.code === tab).label})`}
          value={newPhrase}
          onChange={(e) => setNewPhrase(e.target.value)}
          fullWidth
        />
        <Button
          variant="contained"
          onClick={handleAddPhrase}
          disabled={loading || !newPhrase.trim()}
          sx={{
            minWidth: { xs: "100%", sm: "120px" },
            fontSize: { xs: "1rem", md: "1.1rem" },
            borderRadius: "24px",
            py: { xs: 1, md: 1.5 },
          }}
        >
          Ajouter
        </Button>
      </Box>
      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer
          component={Paper}
          sx={{ marginY: "24px", width: "100%", overflowX: "auto" }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Phrase</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {blacklist[tab].length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} sx={{ textAlign: "center" }}>
                    Aucune phrase dans la blacklist.
                  </TableCell>
                </TableRow>
              ) : (
                blacklist[tab].map((phrase, index) => (
                  <TableRow key={index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell
                      sx={{
                        wordBreak: "break-word",
                        maxWidth: { xs: 120, sm: 300 },
                      }}
                    >
                      {phrase}
                    </TableCell>
                    <TableCell>
                      <Button
                        color="error"
                        onClick={() => handleDeletePhrase(phrase)}
                        disabled={loading}
                        aria-label="Supprimer"
                        sx={{ minWidth: "40px" }}
                      >
                        <DeleteIcon />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
}

export default BlacklistAdmin;
